<script setup lang="ts">
import { ref } from 'vue'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import { Eye, EyeOff, AlertCircle } from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'
import { signInSchema } from '@/utils/auth/validation'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'

const props = withDefaults(defineProps<{ initialEmail?: string }>(), { initialEmail: '' })

const emit = defineEmits<{
  success: [token: string]
  error: [message: string]
  forgotPassword: []
}>()

const { signInWithEmail, isLoading: authLoading, error: authError, clearError: clearAuthError } = useAuth()

const showPassword = ref(false)
const formError = ref<string | null>(null)
const submitCount = ref(0)

const onSubmit = async (values: Record<string, any>) => {
  submitCount.value++
  formError.value = null
  clearAuthError()

  const token = await signInWithEmail(values.email as string, values.password as string)

  if (token) {
    emit('success', token)
  } else {
    formError.value = authError.value || 'Authentication failed'
    emit('error', formError.value)
  }
}
</script>

<template>
  <Form
    :validation-schema="toTypedSchema(signInSchema)"
    :initial-values="{ email: props.initialEmail }"
    @submit="onSubmit"
    class="space-y-4"
    :validate-on-blur="false"
    :validate-on-change="false"
    :validate-on-input="false"
    :validate-on-model-update="submitCount > 0"
  >
    <Alert v-if="formError" variant="destructive" class="mb-4">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ formError }}</AlertDescription>
    </Alert>

    <FormField name="email" v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0">
      <FormItem>
        <FormLabel>Email</FormLabel>
        <FormControl>
          <Input
            type="email"
            placeholder="your@email.com"
            autocomplete="email"
            v-bind="componentField"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField name="password" v-slot="{ componentField }" :validate-on-blur="false" :validate-on-change="false" :validate-on-model-update="submitCount > 0">
      <FormItem>
        <FormLabel>Password</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter your password"
              autocomplete="current-password"
              v-bind="componentField"
              class="pr-10"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/80"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <Eye v-if="!showPassword" class="w-5 h-5" />
              <EyeOff v-else class="w-5 h-5" />
            </button>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="flex justify-end">
      <button
        type="button"
        @click="emit('forgotPassword')"
        class="text-sm text-primary hover:text-primary/80 hover:underline"
      >
        Forgot password?
      </button>
    </div>

    <Button
      type="submit"
      :disabled="authLoading"
      class="w-full h-12 text-base font-medium"
    >
      <span v-if="authLoading">Signing in...</span>
      <span v-else>Sign in</span>
    </Button>
  </Form>
</template>
